import { wizardVariant } from '@euler/functions/apdm/campaignWizard';
import {
  CampaignWizardAttachmentScope,
  CampaignWizardAttachmentTarget as ImageTarget,
  useApdmCampaignWizard,
} from '@euler/functions/apdm/useApdmCampaignWizard';
import {
  AiAttachmentInfo,
  AiAttachmentMimeType,
  ApdmCampaignVariantApplicability,
} from '@euler/model/apdm';
import { useEffect, useRef, useState } from 'react';
import { Text, View } from 'react-native';
import { useTheme } from 'styled-components';
import { ApdmCouponPreview } from '../../coupons/ApdmCouponPreview';
import { ApdmUploadButton } from '../../imports/ApdmUploadButton';
import {
  ReviewAction,
  ReviewChoice,
  ReviewField,
} from '../../imports/ReviewControls';
import { WizardFormProps, WizardToggle } from './WizardControls';

export function WizardContent({
  draft,
  disabled,
  onChange,
  wizard,
  execute,
}: WizardFormProps & {
  wizard: ReturnType<typeof useApdmCampaignWizard>;
  execute: (operation: () => Promise<unknown>) => void;
}) {
  const [instructions, setInstructions] = useState('');
  const [target, setTarget] = useState(ImageTarget.Images);
  const theme = useTheme();
  const [uploadScope, setUploadScope] =
    useState<CampaignWizardAttachmentScope | null>(null);
  const attachmentActions = useRef({
    begin: wizard.actions.beginAttachment,
    cancel: wizard.actions.cancelAttachment,
  });
  attachmentActions.current = {
    begin: wizard.actions.beginAttachment,
    cancel: wizard.actions.cancelAttachment,
  };
  const campaignId = wizard.state.campaign?.id;
  const version = wizard.state.campaign?.version;
  useEffect(() => {
    const actions = attachmentActions.current;
    const ticket = actions.begin(target);
    setUploadScope(ticket);
    return () => actions.cancel(ticket);
  }, [target, campaignId, version]);
  const variants = draft.variants.length
    ? draft.variants
    : Array.from({ length: 3 }, () => wizardVariant('', null));
  const coupon = wizard.state.options?.coupons.find(
    value => value.id === draft.couponCampaignId,
  );
  const uploaded = async (info: AiAttachmentInfo) => {
    if (
      ![AiAttachmentMimeType.Jpeg, AiAttachmentMimeType.Png].includes(
        info.mimeType,
      )
    )
      throw new Error('Only JPEG and PNG attachments are allowed');
    if (uploadScope) wizard.actions.applyAttachment(uploadScope, info);
  };
  return (
    <>
      <Text style={{ color: theme.label.color, marginBottom: 12 }}>
        手动编写 3–5 条文案。变量：
        {'{customerName} {plateNo} {dueItems} {lastVisitDate} {storeName}'}。VIN
        与里程不会成为文案必填字段。
      </Text>
      {variants.map((variant, index) => (
        <View key={index} style={{ marginBottom: 16 }}>
          <ReviewField
            label={`文案 ${index + 1}`}
            multiline
            value={variant.text}
            disabled={disabled}
            onChange={text =>
              onChange({
                ...draft,
                variants: variants.map((value, ordinal) =>
                  ordinal === index
                    ? wizardVariant(text, value.applicability ?? null)
                    : value,
                ),
              })
            }
          />
          <ReviewChoice
            label="这条文案适用的保养依据"
            value={variant.applicability ?? 'unset'}
            disabled={disabled}
            options={[
              { value: 'unset', label: '尚未指定（需人工选择）' },
              {
                value: ApdmCampaignVariantApplicability.Confirmed,
                label: '已确认的保养时间',
              },
              {
                value: ApdmCampaignVariantApplicability.Estimated,
                label: '仅估算／提醒',
              },
              {
                value: ApdmCampaignVariantApplicability.All,
                label: '两者均适用（须谨慎措辞）',
              },
            ]}
            onChange={value =>
              onChange({
                ...draft,
                variants: variants.map((entry, ordinal) =>
                  ordinal === index
                    ? wizardVariant(
                        entry.text,
                        value === 'unset'
                          ? null
                          : (value as ApdmCampaignVariantApplicability),
                      )
                    : entry,
                ),
              })
            }
          />
          <Text style={{ color: theme.label.color }}>
            所需字段：{variant.requiredFields.join('、') || '无'}
          </Text>
          {variants.length > 3 && (
            <ReviewAction
              title="删除此文案"
              disabled={disabled}
              onPress={() =>
                onChange({
                  ...draft,
                  variants: variants.filter((_, ordinal) => ordinal !== index),
                })
              }
            />
          )}
        </View>
      ))}
      <ReviewAction
        title="添加文案"
        disabled={disabled || variants.length >= 5}
        onPress={() =>
          onChange({
            ...draft,
            variants: [...variants, wizardVariant('', null)],
          })
        }
      />
      <ReviewField
        label="给 AI 的补充要求（可选，最多 2000 字）"
        value={instructions}
        disabled={disabled}
        multiline
        onChange={setInstructions}
      />
      <ReviewAction
        title="让 AI 起草 3–5 条候选文案"
        disabled={
          disabled ||
          !uploadScope ||
          wizard.state.dirty.length > 0 ||
          instructions.length > 2000
        }
        onPress={() =>
          execute(() => wizard.actions.writeVariants(instructions))
        }
      />
      <Text style={{ color: theme.label.color, marginTop: 8 }}>
        AI
        候选不会自动保存或确认。先保存已有修改，才可获取与当前版本对应的建议。
      </Text>
      {wizard.state.proposal && (
        <View style={{ marginVertical: 12 }}>
          {wizard.state.proposal.variants.map((variant, index) => (
            <Text
              key={index}
              style={{ color: theme.label.color, marginBottom: 8 }}
            >
              {variant.text} · {variant.applicability ?? '尚未指定'}
            </Text>
          ))}
          <ReviewAction
            title="采用这些候选并继续人工修改"
            disabled={disabled}
            onPress={() =>
              execute(async () => wizard.actions.adopt(wizard.state.proposal!))
            }
          />
        </View>
      )}
      <ReviewChoice
        label="现有优惠券"
        value={draft.couponCampaignId ?? 'none'}
        disabled={disabled}
        options={[
          { value: 'none', label: '不附优惠券' },
          ...(wizard.state.options?.coupons.map(value => ({
            value: value.id,
            label: `${value.title} · ${value.brandName}`,
          })) ?? []),
        ]}
        onChange={value =>
          onChange({
            ...draft,
            couponCampaignId: value === 'none' ? null : value,
            posterTemplateAttachmentId: null,
            content: { ...draft.content, couponLinkCard: false },
          })
        }
      />
      {draft.couponCampaignId && !coupon && (
        <Text style={{ color: theme.label.color }}>
          当前选择不在已加载的券目录中，请刷新或加载更多并重新核对。
        </Text>
      )}
      {wizard.state.options?.nextAfterCouponCampaignId && (
        <ReviewAction
          title="加载更多优惠券"
          disabled={disabled}
          onPress={() => execute(() => wizard.actions.refreshCatalog(true))}
        />
      )}
      {coupon && (
        <ApdmCouponPreview
          title={coupon.title}
          storeName={coupon.brandName}
          startsAt={String(coupon.startsAt)}
          endsAt={String(coupon.endsAt)}
          items={coupon.items}
        />
      )}
      <WizardToggle
        label="附带优惠券链接卡片（默认关闭）"
        value={draft.content.couponLinkCard}
        disabled={disabled || !draft.couponCampaignId}
        onChange={couponLinkCard =>
          onChange({ ...draft, content: { ...draft.content, couponLinkCard } })
        }
      />
      {draft.couponCampaignId && (
        <Text style={{ color: theme.label.color }}>
          默认海报由服务器根据所选优惠券生成，不支持上传替换模板。
        </Text>
      )}
      <WizardToggle
        label="附带网页链接"
        value={!!draft.content.webpageLink}
        disabled={disabled}
        onChange={enabled =>
          onChange({
            ...draft,
            content: {
              ...draft.content,
              webpageLink: enabled
                ? {
                    kind: 'link',
                    url: '',
                    title: '',
                    desc: '',
                    thumbnailAttachmentId: null,
                  }
                : null,
            },
          })
        }
      />
      {draft.content.webpageLink && (
        <>
          <ReviewField
            label="网页 URL（HTTPS）"
            value={draft.content.webpageLink.url}
            disabled={disabled}
            onChange={url =>
              onChange({
                ...draft,
                content: {
                  ...draft.content,
                  webpageLink: { ...draft.content.webpageLink!, url },
                },
              })
            }
          />
          <ReviewField
            label="网页标题"
            value={draft.content.webpageLink.title}
            disabled={disabled}
            onChange={title =>
              onChange({
                ...draft,
                content: {
                  ...draft.content,
                  webpageLink: { ...draft.content.webpageLink!, title },
                },
              })
            }
          />
          <ReviewField
            label="网页简介（可选）"
            value={draft.content.webpageLink.desc ?? ''}
            disabled={disabled}
            onChange={desc =>
              onChange({
                ...draft,
                content: {
                  ...draft.content,
                  webpageLink: { ...draft.content.webpageLink!, desc },
                },
              })
            }
          />
          {draft.content.webpageLink.thumbnailAttachmentId && (
            <ReviewAction
              title={`移除网页缩略图 ${draft.content.webpageLink.thumbnailAttachmentId}`}
              disabled={disabled}
              onPress={() =>
                onChange({
                  ...draft,
                  content: {
                    ...draft.content,
                    webpageLink: {
                      ...draft.content.webpageLink!,
                      thumbnailAttachmentId: null,
                    },
                  },
                })
              }
            />
          )}
        </>
      )}
      <ReviewChoice
        label="图片用途（仅 JPEG／PNG）"
        value={target}
        options={[
          { value: ImageTarget.Images, label: '消息图片' },
          { value: ImageTarget.Thumbnail, label: '网页缩略图' },
        ]}
        disabled={disabled}
        onChange={next => {
          wizard.actions.cancelAttachment(uploadScope);
          setUploadScope(null);
          setTarget(next);
        }}
      />
      <ApdmUploadButton
        mode="image"
        disabled={
          disabled ||
          (target === ImageTarget.Thumbnail && !draft.content.webpageLink)
        }
        onUploaded={uploaded}
      />
      {draft.content.imageAttachmentIds.map(id => (
        <ReviewAction
          key={id}
          title={`移除消息图片 ${id}`}
          disabled={disabled}
          onPress={() =>
            onChange({
              ...draft,
              content: {
                ...draft.content,
                imageAttachmentIds: draft.content.imageAttachmentIds.filter(
                  value => value !== id,
                ),
              },
            })
          }
        />
      ))}
    </>
  );
}
